/**
 * useRoleModules — Vague 99.8.
 *
 * Returns the set of modules this user's role can access, plus a helper
 * `has(key)`. Shared by the sidebar and route guards so the UI stays in sync
 * with the backend ACL (`/api/role-permissions/{role}`).
 */
import { useEffect, useState, useCallback } from 'react';
import api from '../api/client';

let cache = null;
let inflight = null;
const subs = new Set();

function notify() { subs.forEach((fn) => fn(cache)); }

async function load() {
  if (cache) return cache;
  if (inflight) return inflight;
  inflight = api.get('/role-permissions/me').then(({ data }) => {
    cache = data?.modules || {};
    inflight = null;
    notify();
    return cache;
  }).catch(() => {
    cache = {};
    inflight = null;
    notify();
    return cache;
  });
  return inflight;
}

export function invalidateRoleModulesCache() {
  cache = null;
  inflight = null;
  notify();
}

export function useRoleModules() {
  const [modules, setModules] = useState(cache);
  const [loading, setLoading] = useState(!cache);

  useEffect(() => {
    const unsub = (m) => { setModules(m); setLoading(false); };
    subs.add(unsub);
    if (cache) { setLoading(false); } else { load(); }
    return () => { subs.delete(unsub); };
  }, []);

  const has = useCallback((key) => {
    if (!modules) return true; // loading state — optimistic
    return modules[key] !== false;
  }, [modules]);

  return { modules, loading, has };
}
